import React, { useState, useEffect } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft, FaBuilding, FaUserCircle, FaSignOutAlt, FaBell,
  FaExclamationTriangle, FaShieldAlt, FaChartBar, FaBars
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const CompanyHealth = () => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState('');
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadProfile();
    loadAbuseSummary();
  }, [navigate]);

  const loadProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const d = await res.json();
        setUserName(`${d.firstName || d.first_name || ''} ${d.lastName || d.last_name || ''}`);
      }
    } catch (e) {}
  };

  const loadAbuseSummary = async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/company-health/abuse'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTenants(data.tenants || []);
      } else {
        toast.error('Failed to load company abuse summary');
      }
    } catch (e) {
      toast.error('Failed to load company abuse summary');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const tenantsWithAbuse = tenants.filter(t => t.abuse_events_7d > 0);
  const tenantsClean = tenants.filter(t => t.abuse_events_7d === 0);

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge"><FaShieldAlt /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn"><FaBell /></button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header"><FaUserCircle className="cs-menu-avatar" /><div><p className="cs-menu-name">{userName}</p><p className="cs-menu-role">Cloud Staff Admin</p></div></div>
                  <div className="cs-user-menu-divider" />
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout"><FaSignOutAlt /> Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <div className="cs-layout">
        <CloudStaffSidebar active="company-health" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        <main className="cs-content">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #21262d' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Link to="/cloud-staff/dashboard" className="act-back-btn"><FaArrowLeft /></Link>
              <div>
                <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f0f6fc', margin: 0 }}><FaChartBar style={{ marginRight: '0.5rem', color: '#58a6ff' }} /> Company Health</h1>
                <p style={{ fontSize: '0.8rem', color: '#8b949e', margin: '0.2rem 0 0' }}>Abuse pattern summary per tenant (last 7 days)</p>
              </div>
            </div>
            <button onClick={loadAbuseSummary} className="cs-primary-btn" disabled={loading}>Refresh</button>
          </div>

          {loading ? (
            <div className="cs-loading"><div className="cs-spinner" /><p>Loading abuse summary...</p></div>
          ) : (
            <>
              {tenantsWithAbuse.length > 0 && (
                <div className="cs-section">
                  <h2 className="cs-section-title"><FaExclamationTriangle style={{ marginRight: '0.5rem', color: '#ffa657' }} /> Tenants with abuse events (7d)</h2>
                  <div className="cs-activity-table">
                    <table>
                      <thead>
                        <tr><th>Company</th><th>Type</th><th>Code</th><th>Abuse events (7d)</th><th>Most common</th><th>IP count</th><th>IPs</th></tr>
                      </thead>
                      <tbody>
                        {tenantsWithAbuse.map(t => (
                          <tr key={`${t.company_type}-${t.company_id}`}>
                            <td><strong>{t.company_name}</strong></td>
                            <td><span className="cs-badge module">{t.company_type}</span></td>
                            <td style={{ fontFamily: 'monospace' }}>{t.company_code || '—'}</td>
                            <td style={{ color: t.abuse_events_7d > 5 ? '#f85149' : '#ffa657' }}>{t.abuse_events_7d}</td>
                            <td>{t.most_common_event_type ? `${t.most_common_event_type} (${t.most_common_event_count})` : '—'}</td>
                            <td>{t.ip_count}</td>
                            <td style={{ fontSize: '0.75rem', fontFamily: 'monospace', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {t.ip_distribution?.length ? t.ip_distribution.join(', ') : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="cs-section">
                <h2 className="cs-section-title">All tenants</h2>
                <div className="cs-activity-table">
                  <table>
                    <thead>
                      <tr><th>Company</th><th>Type</th><th>Code</th><th>Abuse events (7d)</th><th>Most common</th><th>IP count</th></tr>
                    </thead>
                    <tbody>
                      {tenants.map(t => (
                        <tr key={`${t.company_type}-${t.company_id}`}>
                          <td><strong>{t.company_name}</strong></td>
                          <td><span className="cs-badge module">{t.company_type}</span></td>
                          <td style={{ fontFamily: 'monospace' }}>{t.company_code || '—'}</td>
                          <td style={{ color: t.abuse_events_7d > 0 ? (t.abuse_events_7d > 5 ? '#f85149' : '#ffa657') : '#3fb950' }}>{t.abuse_events_7d}</td>
                          <td>{t.most_common_event_type ? `${t.most_common_event_type} (${t.most_common_event_count})` : '—'}</td>
                          <td>{t.ip_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {tenants.length === 0 && !loading && (
                <div className="cs-empty-state"><FaBuilding /><p>No companies found</p></div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default CompanyHealth;
